import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Banner, Button, Card, formatMoney, PageHeader, Pill } from '@elysian/ui';
import type { SearchResults } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { shortDate } from '../invoices/shared';

const DOC: Record<string, { word: string; href: (id: string) => string }> = {
  sales_invoice: { word: 'Invoice', href: (id) => `/invoices/${id}` },
  sales_credit: { word: 'Credit note', href: (id) => `/invoices/${id}` },
  purchase_bill: { word: 'Bill', href: (id) => `/bills/${id}` },
  purchase_credit: { word: 'Supplier credit', href: (id) => `/bills/${id}` },
};
const STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'draft' | 'info' | 'danger' }> = {
  draft: { label: 'Draft', tone: 'draft' }, posted: { label: 'Unpaid', tone: 'info' }, part_paid: { label: 'Part paid', tone: 'warn' },
  paid: { label: 'Paid', tone: 'ok' }, void: { label: 'Void', tone: 'draft' },
};

/**
 * Search the books: customers and suppliers, invoices and bills (by number,
 * reference, name or amount), journals and accounts. The header's search box
 * comes here.
 */
export function Search() {
  const { source, context } = useSession();
  const base = context?.organisation.base_currency ?? 'EUR';
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [text, setText] = useState(q);
  useEffect(() => setText(q), [q]);
  const data = useAsync<SearchResults | null>(() => (q.trim().length >= 2 ? source.search(q, 20) : Promise.resolve(null)), [source, q]);
  const r = data.state === 'ready' ? data.data : null;
  const total = r ? r.contacts.length + r.documents.length + r.journals.length + r.accounts.length : 0;

  return (
    <>
      <PageHeader title="Search" subtitle={q ? `Results for “${q}”` : 'Names, invoice and bill numbers, references, amounts'} />
      <form role="search" aria-label="Search the books" className="el-row" style={{ gap: 8, marginBottom: 16, flexWrap: 'wrap' }}
        onSubmit={(e) => { e.preventDefault(); setParams(text.trim() ? { q: text.trim() } : {}); }}>
        <label className="el-search" style={{ flex: '1 1 280px', maxWidth: 520 }}>
          <span className="el-vh">Search for</span>
          <input type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder="Murphy, INV-1042, 1,230.00…" autoFocus />
        </label>
        <Button type="submit" variant="primary">Search</Button>
      </form>

      {q.trim().length > 0 && q.trim().length < 2 && <p className="el-muted">Type at least two characters.</p>}
      {data.state === 'error' && <Banner>Couldn’t search: {data.error.message}</Banner>}
      {data.state === 'loading' && q && <div className="el-skeleton" style={{ height: 120 }} aria-label="Searching" />}
      {r && total === 0 && (
        <Card padding="20px">
          <p style={{ margin: 0 }}><strong>Nothing matches “{r.q}”.</strong></p>
          <p className="el-hint" style={{ margin: '6px 0 0' }}>
            Try part of a name, an invoice or bill number, the supplier’s own invoice number, or an amount such as 1,230.00.
          </p>
        </Card>
      )}
      {r && total > 0 && (
        <div className="el-stack">
          {r.amount != null && <p className="el-hint" style={{ margin: 0 }}>Also matching the amount {formatMoney(String(r.amount), base)} in any currency.</p>}
          {r.contacts.length > 0 && (
            <Card as="section" padding="16px 0 8px" aria-labelledby="sr-contacts">
              <div className="el-card-head"><h2 id="sr-contacts" className="el-h2">Customers and suppliers</h2></div>
              <ul className="el-result-list">
                {r.contacts.map((c) => (
                  <li key={c.id}>
                    <Link to={c.is_customer ? `/customers/${c.id}` : `/suppliers/${c.id}`}>{c.name}</Link>
                    <span className="el-hint">
                      {[c.is_customer && 'Customer', c.is_supplier && 'Supplier', c.email, c.currency && c.currency !== base && c.currency].filter(Boolean).join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {r.documents.length > 0 && (
            <Card as="section" padding="16px 0 8px" aria-labelledby="sr-docs">
              <div className="el-card-head"><h2 id="sr-docs" className="el-h2">Invoices and bills</h2></div>
              <div className="el-table-wrap">
                <table className="el-table" aria-label="Invoices and bills found">
                  <thead><tr><th scope="col">Document</th><th scope="col">Who</th><th scope="col">Dated</th><th scope="col">Status</th><th scope="col" className="el-num">Total</th></tr></thead>
                  <tbody>
                    {r.documents.map((d) => {
                      const kind = DOC[d.type]!;
                      const st = STATUS[d.status] ?? { label: d.status, tone: 'info' as const };
                      return (
                        <tr key={d.id}>
                          <td><Link className="el-row-link" to={kind.href(d.id)}>{kind.word} {d.doc_no ?? '(draft)'}</Link>
                            {(d.supplier_ref || d.reference) && <div className="el-hint">{d.supplier_ref ? `Their no. ${d.supplier_ref}` : `Ref ${d.reference}`}</div>}</td>
                          <td>{d.contact_name}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{shortDate(d.issue_date)}</td>
                          <td><Pill tone={st.tone}>{st.label}</Pill></td>
                          <td className="el-num">{formatMoney(String(d.gross_total), d.currency)}
                            {d.currency !== base && <div className="el-hint">{formatMoney(String(d.base_gross_total), base)}</div>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          {r.journals.length > 0 && (
            <Card as="section" padding="16px 0 8px" aria-labelledby="sr-journals">
              <div className="el-card-head"><h2 id="sr-journals" className="el-h2">Journals</h2></div>
              <ul className="el-result-list">
                {r.journals.map((j) => (
                  <li key={j.id}>
                    <Link to={`/journals?open=${j.id}`}>{j.journal_no}</Link>
                    <span>{j.description}</span>
                    <span className="el-hint">{shortDate(j.date)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {r.accounts.length > 0 && (
            <Card as="section" padding="16px 0 8px" aria-labelledby="sr-accounts">
              <div className="el-card-head"><h2 id="sr-accounts" className="el-h2">Accounts</h2></div>
              <ul className="el-result-list">
                {r.accounts.map((a) => (
                  <li key={a.code}>
                    <Link to={`/accounts?q=${encodeURIComponent(a.code)}`}>{a.code} {a.name}</Link>
                    <span className="el-hint">{a.class}{a.is_active ? '' : ' · switched off'}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </>
  );
}
